import React from 'react';
import {
  mutedTextRoleStyle,
  scaledTextRoleStyle,
  type TextRoleStyle,
} from './semanticTextRoles';

/**
 * WEB-926, позиция 8 — шкала ИМТ с ползунком (референс BetterMe: горизонтальный
 * градиент голубой→зелёный→жёлтый→красный, кружок-ползунок, подписи зон снизу,
 * активная — жирным).
 *
 * ЗНАЧЕНИЕ ВПИСЫВАЕТ АВТОР. Блок сознательно НЕ считает ИМТ по ответам:
 * `height_picker` / `weight_picker` кладут в ответ голое число, а переключатель
 * kg/lb и cm/ft живёт в локальном состоянии пикера и в ответ не попадает. То
 * есть «70» — это и 70 кг, и 70 фунтов; посчитать из этого ИМТ = угадать
 * единицу, а ошибка тут медицинская. Автор может подставить сюда свою
 * {{переменную}} (например, посчитанную у него), но выдумывать за него мы не
 * будем: не число → ползунка нет, шкала показывается без позиции.
 *
 * Формулировки зон — тоже авторские. Дефолтные подписи (когда автор их не
 * задал) — числовые диапазоны, без единого медицинского слова: блок не ставит
 * диагнозов и не даёт рекомендаций.
 */

export interface BmiScaleZone {
  id: string;
  label: string;
  upTo: number;
  color?: string;
}

const FALLBACK_MIN = 15;
const FALLBACK_MAX = 40;
/** Границы по умолчанию — только числа, без интерпретаций. */
const FALLBACK_BOUNDS = [18.5, 25, 30];

/** Цвета зон по порядку: холодный → тёплый, как в референсе. */
const ZONE_COLORS = [
  'var(--color-info, #40a9ff)',
  'var(--color-success, #52c41a)',
  'var(--color-warning, #faad14)',
  'var(--color-error, #ff4d4f)',
];

function zoneColor(zone: BmiScaleZone, index: number): string {
  return zone.color || ZONE_COLORS[index % ZONE_COLORS.length];
}

/** Числовая подпись-диапазон для зон, которые автор не подписал. */
function rangeLabel(from: number, to: number): string {
  return `${from}–${to}`;
}

function buildFallbackZones(min: number, max: number): BmiScaleZone[] {
  const bounds = [...FALLBACK_BOUNDS.filter((b) => b > min && b < max), max];
  return bounds.map((upTo, i) => ({
    id: `bmi-default-${i}`,
    label: rangeLabel(i === 0 ? min : bounds[i - 1], upTo),
    upTo,
  }));
}

interface ScaleGeometry {
  min: number;
  max: number;
  zones: BmiScaleZone[];
}

/** Нормализует настройки: перевёрнутые/пустые границы не должны ломать шкалу. */
export function resolveBmiGeometry(
  zones: BmiScaleZone[] | undefined,
  min: number | undefined,
  max: number | undefined,
): ScaleGeometry {
  const safeMin = typeof min === 'number' && Number.isFinite(min) ? min : FALLBACK_MIN;
  const rawMax = typeof max === 'number' && Number.isFinite(max) ? max : FALLBACK_MAX;
  const safeMax = rawMax > safeMin ? rawMax : safeMin + (FALLBACK_MAX - FALLBACK_MIN);
  const authored = (zones ?? []).filter((z) => typeof z.upTo === 'number');
  return {
    min: safeMin,
    max: safeMax,
    zones: authored.length > 0 ? authored : buildFallbackZones(safeMin, safeMax),
  };
}

/** Позиция значения на шкале в процентах; за края не выходит. */
export function bmiPositionPercent(value: number, min: number, max: number): number {
  const pct = ((value - min) / (max - min)) * 100;
  return Math.min(100, Math.max(0, Math.round(pct)));
}

/** Первая зона, в которую попало значение; выше последней границы — последняя. */
export function activeZoneIndex(zones: BmiScaleZone[], value: number): number {
  const found = zones.findIndex((z) => value <= z.upTo);
  return found === -1 ? zones.length - 1 : found;
}

/** Число из авторской строки: «22,5» и «22.5» — одно и то же, мусор → null. */
export function parseBmiValue(raw: string | undefined): number | null {
  if (!raw) return null;
  const normalized = raw.trim().replace(',', '.');
  if (!/^-?\d+(\.\d+)?$/.test(normalized)) return null;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * WEB-1692 юнит 4: отношение кегля ПОДПИСИ НАД ЧИСЛОМ к кеглю самого числа —
 * ровно то, что стоит в вёрстке (13 против 28). Пара «подпись + число» едет
 * как одно целое: автор задаёт число, подпись считается сама (вариант А).
 */
const BMI_VALUE_LABEL_RATIO = 13 / 28;

interface BmiScaleBlockProps {
  title?: React.ReactNode;
  value?: string;
  valueLabel?: string;
  scaleMin?: number;
  scaleMax?: number;
  zones?: BmiScaleZone[];
  note?: string;
  /** Локализованное название шкалы для скринридера (SVG/полосу озвучивать нечем). */
  scaleLabel: string;
  /** WEB-1692 юнит 4: заголовок блока. Пусто = `{}`. */
  titleRole?: TextRoleStyle;
  /**
   * WEB-1692 юнит 4: МЕТКИ ЗОН под шкалой. Вариант А: авторский цвет ставится
   * на активную зону, неактивные приглушаются от него же — так видно, в какую
   * зону попал посетитель.
   */
  zoneLabelsRole?: TextRoleStyle;
  /** WEB-1692 юнит 4: число ИМТ; подпись над ним считается от него. */
  valueRole?: TextRoleStyle;
  /** WEB-1692 юнит 4: текст под метками — третья группа, названная владельцем. */
  noteRole?: TextRoleStyle;
  /**
   * WEB-1677 (возврат приёмки 02.09) — ГОТОВЫЙ ЦВЕТ «Заливки элемента».
   *
   * У трёх соседей волны WEB-926 (размытый тизер, полосы «сейчас/цель»,
   * промо-тикет) настройка работает сама собой: у каждого есть карточка с
   * `background: var(--color-bg-surface, …)`, а обёртка `blockInnerSurface`
   * подменяет эту переменную поддереву блока. У шкалы ИМТ собственной
   * поверхности нет вовсе — единственным читателем переменной был кружок-
   * ползунок 24×24, да и тот рисуется, лишь когда авторское значение оказалось
   * числом (по умолчанию поле пустое). То есть настройка была мертва в
   * состоянии по умолчанию и почти незаметна в остальных.
   *
   * Поэтому цвет приходит сюда ЗНАЧЕНИЕМ, а не переменной (тот же путь, что у
   * «Баннера срочности», WEB-1711): узел красится, только когда автор заливку
   * задал. Без неё стиль корня прежний байт-в-байт — ни фона, ни отступа, ни
   * скругления, — и ни одна опубликованная воронка не меняется ни на пиксель
   * (инвариант WEB-1062 п.5). Переменную обёртка публикует как раньше, ползунок
   * читает её как раньше: ничего не отбирается.
   */
  innerFill?: string;
}

export function BmiScaleBlock({
  title,
  value,
  valueLabel,
  scaleMin,
  scaleMax,
  zones,
  note,
  scaleLabel,
  titleRole = {},
  zoneLabelsRole = {},
  valueRole = {},
  noteRole = {},
  innerFill,
}: BmiScaleBlockProps): React.ReactElement {
  const geometry = resolveBmiGeometry(zones, scaleMin, scaleMax);
  const numeric = parseBmiValue(value);
  const hasValue = numeric !== null;
  const position = hasValue ? bmiPositionPercent(numeric, geometry.min, geometry.max) : null;
  const activeIndex = hasValue ? activeZoneIndex(geometry.zones, numeric) : -1;

  // Плавный градиент: цвет каждой зоны стоит в её центре, CSS смешивает между
  // ними — та же полоса, что в референсе, а границы читаются по подписям.
  const stops = geometry.zones.map((zone, i) => {
    const from = i === 0 ? geometry.min : geometry.zones[i - 1].upTo;
    const center = (from + zone.upTo) / 2;
    return `${zoneColor(zone, i)} ${bmiPositionPercent(center, geometry.min, geometry.max)}%`;
  });

  const ariaLabel = [
    scaleLabel,
    hasValue ? String(value ?? '').trim() : null,
    activeIndex >= 0 ? geometry.zones[activeIndex]?.label : null,
  ]
    .filter(Boolean)
    .join(', ');

  return (
    <div
      data-testid="bmi-scale"
      data-has-value={String(hasValue)}
      style={{
        marginBottom: 16,
        // Паддинг и скругление появляются ВМЕСТЕ с заливкой: без неё цветной
        // подложки нет, и отступ внутри пустой рамки был бы лишним сдвигом
        // раскладки у воронок, которые настройку не трогали.
        ...(innerFill
          ? {
              background: innerFill,
              padding: 16,
              borderRadius: 'var(--radius-card, var(--border-radius-lg, 12px))',
            }
          : {}),
      }}
    >
      {title && (
        <div
          data-testid="bmi-scale-title"
          style={{
            fontSize: 18,
            fontWeight: 600,
            marginBottom: 12,
            textAlign: 'center',
            ...titleRole,
          }}
        >
          {title}
        </div>
      )}

      {hasValue && (
        <div style={{ textAlign: 'center', marginBottom: 12 }}>
          {valueLabel && (
            <div
              data-testid="bmi-scale-value-label"
              style={{
                fontSize: 13,
                color: 'var(--color-text-secondary, #595959)',
                // WEB-1692 юнит 4: подпись над числом едет ВМЕСТЕ с числом —
                // кегль отношением, цвет с приглушением (вариант А).
                ...scaledTextRoleStyle(valueRole, BMI_VALUE_LABEL_RATIO),
                ...mutedTextRoleStyle(valueRole),
              }}
            >
              {valueLabel}
            </div>
          )}
          <div
            data-testid="bmi-scale-value"
            style={{
              fontSize: 28,
              fontWeight: 700,
              color: 'var(--color-text-primary, rgba(0,0,0,0.88))',
              ...valueRole,
            }}
          >
            {value}
          </div>
        </div>
      )}

      <div style={{ position: 'relative', paddingTop: 6, paddingBottom: 6 }}>
        <div
          data-testid="bmi-scale-track"
          role="img"
          aria-label={ariaLabel}
          style={{
            height: 12,
            borderRadius: 999,
            background: `linear-gradient(90deg, ${stops.join(', ')})`,
          }}
        />
        {position !== null && (
          <div
            data-testid="bmi-scale-marker"
            data-position={String(position)}
            style={{
              position: 'absolute',
              top: 0,
              left: `${position}%`,
              transform: 'translateX(-50%)',
              width: 24,
              height: 24,
              borderRadius: '50%',
              background: 'var(--color-bg-surface, #fff)',
              border: '3px solid var(--color-text-primary, rgba(0,0,0,0.88))',
              boxSizing: 'border-box',
            }}
          />
        )}
      </div>

      <div style={{ display: 'flex', gap: 4, marginTop: 8 }}>
        {geometry.zones.map((zone, i) => {
          const from = i === 0 ? geometry.min : geometry.zones[i - 1].upTo;
          const width = Math.max(zone.upTo - from, 0.1);
          const isActive = i === activeIndex;
          return (
            <div
              key={zone.id}
              data-testid={`bmi-scale-zone-${i}`}
              data-active={String(isActive)}
              style={{
                flexGrow: width,
                flexBasis: 0,
                minWidth: 0,
                fontSize: 11,
                lineHeight: 1.3,
                textAlign: 'center',
                fontWeight: isActive ? 700 : 400,
                color: isActive
                  ? 'var(--color-text-primary, rgba(0,0,0,0.88))'
                  : 'var(--color-text-tertiary, #8c8c8c)',
                // WEB-1692 юнит 4, вариант А: авторский цвет — активной зоне,
                // неактивные получают его же с прозрачностью. Жирность
                // активной (700 против 400) остаётся: это второй, независимый
                // признак того же смысла.
                ...zoneLabelsRole,
                ...(isActive ? {} : mutedTextRoleStyle(zoneLabelsRole)),
              }}
            >
              {zone.label}
            </div>
          );
        })}
      </div>

      {note && (
        <div
          data-testid="bmi-scale-note"
          style={{
            marginTop: 10,
            textAlign: 'center',
            fontSize: 12,
            color: 'var(--color-text-tertiary, #8c8c8c)',
            ...noteRole,
          }}
        >
          {note}
        </div>
      )}
    </div>
  );
}
